/**
 * Organization Settings - Members Section
 * Manage team members: invite, remove, change roles
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MoreVertical, UserMinus, UserPlus, Shield, AlertTriangle, X, Mail, Trash2, Clock } from 'lucide-react';
import { useOrganization } from '@clerk/clerk-react';
import { useAuth, OrgRole } from '../../../contexts/AuthContext';
import { apiPost, apiDelete, apiGet } from '../../../utils/api';

interface MemberData {
    id: string;
    userId?: string;
    role: OrgRole;
    publicUserData: {
        userId?: string;
        firstName: string | null;
        lastName: string | null;
        imageUrl: string;
        identifier: string;
    };
    createdAt: Date;
}

export function OrganizationSettingsMembers() {
    const { organization, isLoaded, memberships } = useOrganization({
        memberships: {
            infinite: true,
            keepPreviousData: true,
        },
    });
    const { currentOrg, canInviteMembers, orgRole, user, canManageOrg } = useAuth();

    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState<MemberData | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [isInviting, setIsInviting] = useState(false);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    
    // Invitations state
    const [invitations, setInvitations] = useState<any[]>([]);
    const [awaitingFirstLogin, setAwaitingFirstLogin] = useState<any[]>([]);
    const [loadingInvitations, setLoadingInvitations] = useState(true);
    const [invitationToRevoke, setInvitationToRevoke] = useState<any | null>(null);
    const [isRevoking, setIsRevoking] = useState(false);

    const members = (memberships?.data || []) as unknown as MemberData[];
    const usedSlots = currentOrg?.membersCount || 0;
    const maxSlots = currentOrg?.maxMembers || 5;

    // Track if invitations have been fetched
    const invitationsFetchedRef = useRef(false);

    // Fetch pending invitations and users awaiting first login
    const fetchInvitations = useCallback(async (force = false) => {
        // Skip if already fetched unless forced
        if (invitationsFetchedRef.current && !force) {
            return;
        }

        try {
            setLoadingInvitations(true);
            const response = await apiGet('/organization/pending');
            
            if (response.status === 'success') {
                setInvitations(response.data?.pending_invitations || []);
                setAwaitingFirstLogin(response.data?.awaiting_first_login || []);
            } else {
                // Fallback to Clerk's client-side method
                if (organization) {
                    const fetchedInvitations = await organization.getInvitations();
                    const pendingInvitations = fetchedInvitations.data?.filter(
                        (inv: any) => inv.status === 'pending'
                    ) || [];
                    setInvitations(pendingInvitations);
                }
            }
            invitationsFetchedRef.current = true;
        } catch (error) {
            console.error('Failed to fetch pending users:', error);
            // Fallback to Clerk's client-side method
            if (organization) {
                try {
                    const fetchedInvitations = await organization.getInvitations();
                    const pendingInvitations = fetchedInvitations.data?.filter(
                        (inv: any) => inv.status === 'pending'
                    ) || [];
                    setInvitations(pendingInvitations);
                } catch (e) {
                    console.error('Failed to fetch invitations from Clerk:', e);
                }
            }
        } finally {
            setLoadingInvitations(false);
        }
    }, [organization]);

    // Fetch invitations once when organization is available
    useEffect(() => {
        if (organization?.id && !invitationsFetchedRef.current) {
            fetchInvitations();
        }
    }, [organization?.id, fetchInvitations]);

    // Revoke invitation handler
    const handleRevokeInvitation = async () => {
        if (!invitationToRevoke) return;
        setIsRevoking(true);
        try {
            await invitationToRevoke.revoke();
            setInvitations(invitations.filter(inv => inv.id !== invitationToRevoke.id));
            showToast('success', 'Invitation revoked successfully');
            setInvitationToRevoke(null);
        } catch (error) {
            console.error('Failed to revoke invitation:', error);
            showToast('error', 'Failed to revoke invitation');
        } finally {
            setIsRevoking(false);
        }
    };

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };

    const getRoleBadge = (role: OrgRole) => {
        switch (role) {
            case 'org:owner':
                return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">Owner</span>;
            case 'org:admin':
                return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Admin</span>;
            default:
                return <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">Member</span>;
        }
    };

    const canRemoveMember = (memberRole: OrgRole): boolean => {
        if (orgRole === 'org:owner') return memberRole !== 'org:owner';
        if (orgRole === 'org:admin') return memberRole === 'org:member';
        return false;
    };

    const canChangeRole = (memberRole: OrgRole): boolean => {
        if (orgRole === 'org:owner') return memberRole !== 'org:owner';
        return false;
    };

    const handleRemoveClick = (member: MemberData) => {
        setMemberToRemove(member);
        setShowRemoveModal(true);
        setOpenMenuId(null);
    };

    const handleRemoveMember = async () => {
        if (!memberToRemove) return;

        const userId = memberToRemove.publicUserData?.userId || memberToRemove.userId;
        if (!userId) {
            showToast('error', 'Unable to identify member');
            return;
        }

        setIsRemoving(true);
        try {
            const response = await apiDelete(`/organization/members/${userId}`);
            
            if (response.status === 'success') {
                showToast('success', `${memberToRemove.publicUserData.identifier} has been removed.`);
                setShowRemoveModal(false);
                setMemberToRemove(null);
            } else {
                showToast('error', response.message || 'Failed to remove member');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 
                                 (error instanceof Error ? error.message : 'Failed to remove member');
            showToast('error', errorMessage);
        } finally {
            setIsRemoving(false);
        }
    };

    const handleChangeRole = async (member: MemberData, newRole: 'org:admin' | 'org:member') => {
        if (!organization) return;

        const userId = member.publicUserData?.userId || member.userId;
        if (!userId) {
            showToast('error', 'Unable to identify member');
            return;
        }

        try {
            await organization.updateMember({
                userId: userId,
                role: newRole,
            });
            showToast('success', `${member.publicUserData.identifier}'s role has been updated.`);
            setOpenMenuId(null);
        } catch (error) {
            showToast('error', error instanceof Error ? error.message : 'Failed to update role');
        }
    };

    const handleInvite = async () => {
        if (!inviteEmail.trim()) return;

        setIsInviting(true);
        try {
            const response = await apiPost('/organization/invite', {
                email: inviteEmail.trim(),
                role: 'org:member',
            });
            
            if (response.status === 'success') {
                const addedDirectly = response.data?.added_directly;
                const emailSent = response.data?.email_sent;
                if (addedDirectly) {
                    showToast('success', `${inviteEmail} has been added to the organization`);
                } else if (emailSent) {
                    showToast('success', `Invitation sent to ${inviteEmail}`);
                } else {
                    showToast('success', `Invitation created for ${inviteEmail}`);
                }
                setShowInviteModal(false);
                setInviteEmail('');
                // Refresh invitations list after successful invite
                fetchInvitations(true);
            } else {
                showToast('error', response.message || 'Failed to send invitation');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 
                                 (error instanceof Error ? error.message : 'Failed to send invitation');
            showToast('error', errorMessage);
        } finally {
            setIsInviting(false);
        }
    };

    if (!isLoaded) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center space-x-4">
                                <div className="w-10 h-10 bg-slate-200 rounded-full animate-pulse" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
                                    <div className="h-3 w-48 bg-slate-200 rounded animate-pulse" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!canManageOrg) {
        return (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                        <h3 className="font-medium text-blue-900">Access Restricted</h3>
                        <p className="text-sm text-blue-700 mt-1">
                            Only organization owners and admins can manage team members.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
                    toast.type === 'success' 
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' 
                        : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                    <p className="text-sm font-medium">{toast.message}</p>
                </div>
            )}

            {/* Header */}
            <div>
                <h2 className="text-xl font-semibold text-slate-900">Team Members</h2>
                <p className="text-slate-500 text-sm mt-1">
                    Manage your organization's team members and their roles
                </p>
            </div>

            {/* Members Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-semibold text-slate-900">Members</h3>
                            <p className="text-sm text-slate-500">{usedSlots} of {maxSlots} seats used</p>
                        </div>
                        {canInviteMembers && usedSlots < maxSlots && (
                            <button
                                onClick={() => setShowInviteModal(true)}
                                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors flex items-center space-x-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                <span>Invite Members</span>
                            </button>
                        )}
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-3 w-full bg-slate-100 rounded-full h-2">
                        <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                                usedSlots >= maxSlots ? 'bg-red-500' : 'bg-emerald-500'
                            }`}
                            style={{ width: `${(usedSlots / maxSlots) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Members Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-100">
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Member</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Joined</th>
                                <th className="w-12 px-6 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {members.map((member) => {
                                const memberId = member.publicUserData?.userId || member.userId;
                                const isCurrentUser = memberId === user?.id;
                                const memberName = member.publicUserData.firstName && member.publicUserData.lastName
                                    ? `${member.publicUserData.firstName} ${member.publicUserData.lastName}`
                                    : member.publicUserData.identifier;

                                return (
                                    <tr key={member.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                {member.publicUserData.imageUrl ? (
                                                    <img 
                                                        src={member.publicUserData.imageUrl} 
                                                        alt={memberName}
                                                        className="w-10 h-10 rounded-full"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-medium">
                                                        {memberName.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="font-medium text-slate-900">{memberName}</span>
                                                        {isCurrentUser && (
                                                            <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">You</span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-slate-500">{member.publicUserData.identifier}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {getRoleBadge(member.role)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {new Date(member.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            {!isCurrentUser && (canRemoveMember(member.role) || canChangeRole(member.role)) && (
                                                <div className="relative">
                                                    <button
                                                        onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
                                                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                                    >
                                                        <MoreVertical className="w-4 h-4 text-slate-500" />
                                                    </button>
                                                    {openMenuId === member.id && (
                                                        <>
                                                            <div 
                                                                className="fixed inset-0 z-40" 
                                                                onClick={() => setOpenMenuId(null)}
                                                            />
                                                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                                                                {canChangeRole(member.role) && (
                                                                    <>
                                                                        {member.role === 'org:member' && (
                                                                            <button
                                                                                onClick={() => handleChangeRole(member, 'org:admin')}
                                                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                                            >
                                                                                <Shield className="w-4 h-4" />
                                                                                <span>Make Admin</span>
                                                                            </button>
                                                                        )}
                                                                        {member.role === 'org:admin' && (
                                                                            <button
                                                                                onClick={() => handleChangeRole(member, 'org:member')}
                                                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2"
                                                                            >
                                                                                <Shield className="w-4 h-4" />
                                                                                <span>Remove Admin</span>
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {canRemoveMember(member.role) && (
                                                                    <button
                                                                        onClick={() => handleRemoveClick(member)}
                                                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                                                                    >
                                                                        <UserMinus className="w-4 h-4" />
                                                                        <span>Remove from Organization</span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {members.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-slate-500">No members found</p>
                    </div>
                )}
            </div>

            {/* Pending Invitations Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-6 py-4 border-b border-slate-100">
                    <div className="flex items-center space-x-2">
                        <Mail className="w-5 h-5 text-slate-400" />
                        <h3 className="text-base font-semibold text-slate-900">
                            Pending Invitations
                            {(invitations.length + awaitingFirstLogin.length) > 0 && (
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                                    {invitations.length + awaitingFirstLogin.length}
                                </span>
                            )}
                        </h3>
                    </div>
                </div>

                {loadingInvitations ? (
                    <div className="p-6">
                        <div className="space-y-4">
                            {[1, 2].map((i) => (
                                <div key={i} className="flex items-center space-x-4">
                                    <div className="w-10 h-10 bg-slate-200 rounded-full animate-pulse" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 w-48 bg-slate-200 rounded animate-pulse" />
                                        <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (invitations.length === 0 && awaitingFirstLogin.length === 0) ? (
                    <div className="text-center py-12">
                        <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No pending invitations</p>
                        <p className="text-sm text-slate-400 mt-1">
                            Invitations you send will appear here until accepted
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Sent</th>
                                    <th className="w-12 px-6 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {/* Pending Invitations */}
                                {invitations.map((invitation) => (
                                    <tr key={invitation.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center text-white font-medium">
                                                    {(invitation.email || invitation.emailAddress)?.charAt(0).toUpperCase() || '?'}
                                                </div>
                                                <div>
                                                    <span className="font-medium text-slate-900">{invitation.email || invitation.emailAddress}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {invitation.role === 'org:admin' ? (
                                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Admin</span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">Member</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-1">
                                                <Clock className="w-3 h-3 text-amber-500" />
                                                <span className="text-xs text-amber-600">Awaiting acceptance</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {invitation.createdAt || invitation.created_at ? new Date(invitation.createdAt || invitation.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => setInvitationToRevoke(invitation)}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Revoke invitation"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {/* Users Awaiting First Login */}
                                {awaitingFirstLogin.map((user) => (
                                    <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                                                    {user.first_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?'}
                                                </div>
                                                <div>
                                                    <span className="font-medium text-slate-900">{user.email}</span>
                                                    {user.first_name && (
                                                        <div className="text-xs text-slate-500">
                                                            {user.first_name} {user.last_name}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {user.role === 'org:admin' ? (
                                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Admin</span>
                                            ) : (
                                                <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">Member</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-1">
                                                <Clock className="w-3 h-3 text-blue-500" />
                                                <span className="text-xs text-blue-600">Awaiting first login</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            {/* No action for awaiting login - they're already members */}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setShowInviteModal(false)} />
                    <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                        <button
                            onClick={() => setShowInviteModal(false)}
                            className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-slate-500" />
                        </button>
                        <h3 className="text-lg font-semibold text-slate-900 mb-4">Invite Team Member</h3>
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm text-blue-800">
                                    The person you invite will be added to your organization <span className="font-semibold">{currentOrg?.name || 'Axone'}</span> and will receive an email with a link to join.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="colleague@company.com"
                                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowInviteModal(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleInvite}
                                    disabled={isInviting || !inviteEmail.trim()}
                                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                >
                                    {isInviting && (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    )}
                                    <span>Send Invitation</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Confirmation Modal */}
            {showRemoveModal && memberToRemove && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setShowRemoveModal(false)} />
                    <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Remove Member</h3>
                        <p className="text-slate-600 mb-6">
                            Are you sure you want to remove{' '}
                            <span className="font-semibold">{memberToRemove.publicUserData.identifier}</span>{' '}
                            from the organization? They will lose access to all organization resources.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowRemoveModal(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemoveMember}
                                disabled={isRemoving}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                {isRemoving && (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                )}
                                <span>Remove</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Revoke Invitation Confirmation Modal */}
            {invitationToRevoke && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setInvitationToRevoke(null)} />
                    <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">Revoke Invitation</h3>
                        <p className="text-slate-600 mb-6">
                            Are you sure you want to revoke the invitation sent to{' '}
                            <span className="font-semibold">{invitationToRevoke.emailAddress}</span>?
                            They will no longer be able to join the organization using this invitation.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setInvitationToRevoke(null)}
                                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRevokeInvitation}
                                disabled={isRevoking}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                {isRevoking && (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                )}
                                <span>Revoke</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrganizationSettingsMembers;
